import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import OTPVerification from '../components/auth/OTPVerification';
import LanguageSwitcher from '../components/LanguageSwitcher';

function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpRequired, setOtpRequired] = useState(false);
  const [temporaryToken, setTemporaryToken] = useState(null);
  const { login, user, completeOTPVerification } = useAuth();
  const navigate = useNavigate();

  // Redirect already logged-in users based on their role
  React.useEffect(() => {
    if (user) {
      const roleRedirects = {
        student:          '/student-portal',
        parent:           '/parent-portal',
        teacher:          '/teacher-portal',
        super_admin:      '/super-admin-portal',
        security_officer: '/security',
        driver:           '/security/driver',
        hr:               '/hr-portal',
        nurse:            '/nurse-portal',
        registrar:        '/registrar-portal',
        storekeeper:      '/storekeeper-portal',
        canteen_staff:    '/canteen-admin',
      };
      navigate(roleRedirects[user.role] || '/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await login(email, password);
      
      // Check if OTP is required
      if (response.status === 'otp_required') {
        setTemporaryToken(response.temporary_token);
        setOtpRequired(true);
        toast.info(`OTP sent to your ${response.otp_method}`);
      } else {
        toast.success('Welcome back!');
        // Navigation will be handled by the useEffect above
      }
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerificationSuccess = (response) => {
    // Update user context with verified user info
    if (response.user && response.access_token) {
      completeOTPVerification(response.user, response.access_token);
      
      setOtpRequired(false);
      setEmail('');
      setPassword('');
      toast.success('Successfully logged in!');
      
      // Navigate based on role
      const roleRedirects = {
        student:          '/student-portal',
        parent:           '/parent-portal',
        teacher:          '/teacher-portal',
        super_admin:      '/super-admin-portal',
        security_officer: '/security',
        driver:           '/security/driver',
        hr:               '/hr-portal',
        nurse:            '/nurse-portal',
        registrar:        '/registrar-portal',
        storekeeper:      '/storekeeper-portal',
        canteen_staff:    '/canteen-admin',
      };
      navigate(roleRedirects[response.user.role] || '/dashboard');
    }
  };

  if (otpRequired && temporaryToken) {
    return (
      <OTPVerification
        email={email}
        temporaryToken={temporaryToken}
        onVerificationSuccess={handleOTPVerificationSuccess}
        onResendOTP={() => {}} // Handled by OTPVerification component
      />
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">
            <svg viewBox="0 0 40 40" className="logo-icon">
              <rect x="4" y="8" width="32" height="24" rx="2" fill="currentColor" opacity="0.2"/>
              <rect x="8" y="12" width="24" height="16" rx="1" fill="currentColor"/>
              <path d="M20 18l8-4v12l-8 4-8-4V14l8 4z" fill="white"/>
            </svg>
          </div>
          <h1>Campusio</h1>
          <p>{t('login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form" data-testid="login-form">
          <div className="form-group">
            <label>{t('login.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
              disabled={isLoading}
              data-testid="email-input"
            />
          </div>

          <div className="form-group">
            <label>{t('login.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.passwordPlaceholder')}
              required
              disabled={isLoading}
              data-testid="password-input"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={isLoading}
            data-testid="login-button"
          >
            {isLoading ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        <div className="security-info">
          <svg className="lock-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="12" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            <line x1="12" y1="16" x2="12" y2="20"></line>
          </svg>
          <span>Your account is protected with two-factor authentication</span>
        </div>

        <div className="auth-footer">
          <p>{t('login.contactAdmin')}</p>
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
